import { Table } from '@tanstack/react-table';
import { debounce } from 'lodash-es';
import React, { useCallback, useLayoutEffect, useMemo, useRef } from 'react';
import { vars } from 'src/core/themes';

interface UseActionColumnAutoConfigOptions {
  actionColId?: string;
  padding?: number;
}

export function useActionColumnAutoConfig<T>(
  table: Table<T>,
  options: UseActionColumnAutoConfigOptions = {}
) {
  const { actionColId = 'actions', padding = 0 } = options;

  const measurementRefsRef = useRef<Map<string, HTMLDivElement>>(new Map());

  // Find action column ID
  const actionColumnId = table
    .getAllColumns()
    .find((col) => col.id === actionColId)?.id;

  // Measure and update column width
  const updateColumnWidth = useCallback(() => {
    if (!actionColumnId) return;

    let maxWidth = 0;

    // Measure all registered cells
    measurementRefsRef.current.forEach((element) => {
      if (element) {
        const width = element.getBoundingClientRect().width;
        maxWidth = Math.max(maxWidth, width);
      }
    });

    // Update column width if we found measurements
    if (maxWidth > 0) {
      const totalWidth = maxWidth + padding;
      const currentSize = table.getColumn(actionColumnId)?.getSize() || 0;
      if (currentSize !== totalWidth) {
        // Update column sizing
        table.setColumnSizing((prev) => {
          const newSizing = {
            ...prev,
            [actionColumnId]: totalWidth,
          };
          return newSizing;
        });
      }
    }
  }, [table, actionColumnId, padding]);

  const debouncedUpdateColumnWidth = useMemo(
    () => debounce(() => updateColumnWidth(), 0),
    [updateColumnWidth]
  );

  // Callback to register measurement refs (NO calculation here - just registration)
  const registerMeasurementRef = useCallback(
    (rowId: string, ref: HTMLDivElement | null) => {
      if (ref) {
        measurementRefsRef.current.set(rowId, ref);
        debouncedUpdateColumnWidth();
      } else {
        measurementRefsRef.current.delete(rowId);
      }
    },
    [debouncedUpdateColumnWidth]
  );

  // Track states that affect which rows are visible
  const pageIndex = table.getState().pagination?.pageIndex ?? 0;
  const pageSize = table.getState().pagination?.pageSize ?? 10;
  const sorting = table.getState().sorting;
  const sortingKey = JSON.stringify(sorting);
  const expanded = table.getState().expanded;
  const expandedKey = JSON.stringify(expanded);

  // Only recalculate when visible rows actually change (NOT on scroll)
  useLayoutEffect(() => {
    updateColumnWidth();
  }, [pageIndex, pageSize, sortingKey, expandedKey, updateColumnWidth]);

  const ActionCellWrapper = useCallback(
    ({ rowId, children }: { rowId: string; children: React.ReactNode }) => {
      return (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: vars.spacing.xs,
            width: '100%',
          }}
        >
          <div
            ref={(ref) => registerMeasurementRef(rowId, ref)}
            style={{ width: 'fit-content' }}
          >
            {children}
          </div>
        </div>
      );
    },
    [registerMeasurementRef]
  );

  return {
    ActionCellWrapper,
  };
}
